import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import settingApi, {
  AdminSettingDto,
  AdminSettingReqDto,
  ApplySettingDto,
  ApplySettingReqDto,
  CommonSettingApiDto,
  CommonSettingDto,
  CommonSettingReqDto,
  DecryptSettingDto,
  DecryptSettingReqDto,
  NotiSettingDto,
  NotiSettingReqDto,
  PrintClientSettingDto,
  PrintClientSettingReqDto,
  PrintFileSettingDto,
  PrintFileSettingReqDto,
  PrintPeriodSettingDto,
  PrintPeriodSettingReqDto,
  ProvisionalSettingDto,
  ProvisionalSettingReqDto,
  ProxySettingDto,
  ProxySettingReqDto,
  ScreenSettingDto,
  ScreenSettingReqDto,
  SettingDto,
  SettingReqDto,
  SubSettingApiDto,
  TakeoutSettingDto,
  TakeoutSettingReqDto,
  UseYN,
} from 'src/api/settingApi';
import { DeepReadonly } from 'src/shared/types/utilityTypes';
import { RootState } from 'src/store';

// types
export type SettingEntity = Omit<SettingDto, 'name' | 'updatedTs' | 'useYn'> & { enabled: boolean };
export interface AdminSettingPartialEntity {
  useUrgency: boolean;
}
export interface ApplySettingPartialEntity {
  allowApproverSameUser: boolean;
  useExcludeApproverUser: boolean;
  excludeApproverUser: string[];
}
export interface CommonSettingPartialEntity {
  locale: string;
}
export interface NotiSettingPartialEntity {
  useMail: boolean;
  useFpns: boolean;
  useWeb: boolean;
  useLongTermPeddingApprovalNotificationDay: boolean;
  longTermPeddingApprovalNotificationDay: number;
}
export interface ProxySettingPartialEntity {
  useLimitDay: boolean;
  limitDay: number;
  useLimitPerson: boolean;
  limitPerson: number;
  allowAllDept: boolean;
}
export interface DecryptSettingPartialEntity {
  useSubmission: boolean;
  useTotalFileSizeMB: boolean;
  totalFileSizeMB: number;
  storagePeriod: number;
  useAllowExtension: boolean;
  allowExtensions: string[];
  useOrder: boolean;
  maxTotalFiles: number;
}
export interface ProvisionalSettingPartialEntity {
  useTotalFileSizeMB: boolean;
  totalFileSizeMB: number;
  storagePeriod: number;
  useAttachFile: boolean;
}
export interface ScreenSettingPartialEntity {
  useLimitPeriod: boolean;
  limitPeriod: number;
  unit: 'DAY'; // 'DAY' | 'WEEK' | 'MONTH' 중 DAY로 고정함
}
export interface TakeoutSettingPartialEntity {
  useLimitPeriod: boolean;
  limitPeriod: number;
  unit: 'DAY'; // 'DAY' | 'WEEK' | 'MONTH' 중 DAY로 고정함
}
export interface PrintPeriodSettingPartialEntity {
  useLimitPeriod: boolean;
  limitPeriod: number;
  unit: 'DAY'; // 'DAY' | 'WEEK' | 'MONTH' 중 DAY로 고정함
}
export interface PrintFileSettingPartialEntity {
  useSubmission: boolean;
  useTotalFileSizeMB: boolean;
  totalFileSizeMB: number;
  storagePeriod: number;
  useAllowExtension: boolean;
  allowExtensions: string[];
  maxTotalFiles: number;
  dsdCode: string;
}
export interface PrintClientSettingPartialEntity {
  useLimitPeriod: boolean;
  limitPeriod: number;
  unit: 'DAY'; // 'DAY' | 'WEEK' | 'MONTH' 중 DAY로 고정함
}

export type AdminSettingEntity = SettingEntity & AdminSettingPartialEntity;
export type ApplySettingEntity = SettingEntity & ApplySettingPartialEntity;
export type CommonSettingEntity = SettingEntity & CommonSettingPartialEntity;
export type NotiSettingEntity = SettingEntity & NotiSettingPartialEntity;
export type ProxySettingEntity = SettingEntity & ProxySettingPartialEntity;
export type DecryptSettingEntity = SettingEntity & DecryptSettingPartialEntity;
export type ProvisionalSettingEntity = SettingEntity & ProvisionalSettingPartialEntity;
export type ScreenSettingEntity = SettingEntity & ScreenSettingPartialEntity;
export type TakeoutSettingEntity = SettingEntity & TakeoutSettingPartialEntity;
export type PrintPeriodSettingEntity = SettingEntity & PrintPeriodSettingPartialEntity;
export type PrintFileSettingEntity = SettingEntity & PrintFileSettingPartialEntity;
export type PrintClientSettingEntity = SettingEntity & PrintClientSettingPartialEntity;

export type SettingReqEntity = SettingEntity;
export type AdminSettingReqEntity = AdminSettingEntity;
export type ApplySettingReqEntity = ApplySettingEntity;
export type CommonSettingReqEntity = CommonSettingEntity;
export type NotiSettingReqEntity = NotiSettingEntity;
export type ProxySettingReqEntity = ProxySettingEntity;
export type DecryptSettingReqEntity = DecryptSettingEntity;
export type ProvisionalSettingReqEntity = ProvisionalSettingEntity;
export type ScreenSettingReqEntity = ScreenSettingEntity;
export type TakeoutSettingReqEntity = TakeoutSettingEntity;
export type PrintPeriodSettingReqEntity = PrintPeriodSettingEntity;
export type PrintFileSettingReqEntity = PrintFileSettingEntity;
export type PrintClientSettingReqEntity = PrintClientSettingEntity;

export interface SettingEntities {
  admin: AdminSettingEntity;
  apply: ApplySettingEntity;
  common: CommonSettingEntity;
  noti: NotiSettingEntity;
  proxy: ProxySettingEntity;
  decrypt: DecryptSettingEntity;
  provisional: ProvisionalSettingEntity;
  screen: ScreenSettingEntity;
  takeout: TakeoutSettingEntity;
  printPeriod: PrintPeriodSettingEntity;
  printFile: PrintFileSettingEntity;
  printClient: PrintClientSettingEntity;
}

function toBoolean(useYN: UseYN): boolean {
  return useYN === 'Y';
}
function toUseYN(b: boolean): UseYN {
  return b ? 'Y' : 'N';
}

function toSettingEntities(
  commonSettingApiDtos: CommonSettingApiDto[],
  subSettingApiDtos: SubSettingApiDto[]
): SettingEntities {
  const settingEntities = createDefaultSettingEntities();

  commonSettingApiDtos
    .map((apiDto) => apiDto.globalSetting)
    .forEach((dto) => {
      if (dto.name === 'ADMIN') {
        settingEntities.admin = toAdminSettingEntity(dto as AdminSettingDto);
      } else if (dto.name === 'APPLY') {
        settingEntities.apply = toApplySettingEntity(dto as ApplySettingDto);
      } else if (dto.name === 'COMMON') {
        settingEntities.common = toCommonSettingEntity(dto as CommonSettingDto);
      } else if (dto.name === 'NOTI') {
        settingEntities.noti = toNotiSettingEntity(dto as NotiSettingDto);
      } else if (dto.name === 'PROXY') {
        settingEntities.proxy = toProxySettingEntity(dto as ProxySettingDto);
      }
    });

  subSettingApiDtos
    .map((apiDto) => apiDto.globalSubSetting)
    .forEach((dto) => {
      if (dto.name === 'DECRYPT') {
        settingEntities.decrypt = toDecryptSettingEntity(dto as DecryptSettingDto);
      } else if (dto.name === 'PROVISIONAL') {
        settingEntities.provisional = toProvisionalSettingEntity(dto as ProvisionalSettingDto);
      } else if (dto.name === 'SCREEN') {
        settingEntities.screen = toScreenSettingEntity(dto as ScreenSettingDto);
      } else if (dto.name === 'TAKEOUT') {
        settingEntities.takeout = toTakeoutSettingEntity(dto as TakeoutSettingDto);
      } else if (dto.name === 'PRINTPOLICY') {
        settingEntities.printPeriod = toPrintPeriodSettingEntity(dto as PrintPeriodSettingDto);
      } else if (dto.name === 'PRINTFILE') {
        settingEntities.printFile = toPrintFileSettingEntity(dto as PrintFileSettingDto);
      } else if (dto.name === 'PRINTCLIENT') {
        settingEntities.printClient = toPrintClientSettingEntity(dto as PrintClientSettingDto);
      }
    });

  return settingEntities;
}
function toSettingEntity(dto: SettingDto): SettingEntity {
  return {
    ..._.omit(dto, ['name', 'updatedTs', 'useYn']),
    enabled: toBoolean(dto.useYn),
  };
}
function toAdminSettingEntity(dto: AdminSettingDto): AdminSettingEntity {
  return {
    ...toSettingEntity(_.omit(dto, ['useUrgency'])),
    useUrgency: toBoolean(dto.useUrgency),
  };
}
function toApplySettingEntity(dto: ApplySettingDto): ApplySettingEntity {
  return {
    ...toSettingEntity(_.omit(dto, ['allowApproverSameUser', 'excludeApproverUser'])),
    allowApproverSameUser: toBoolean(dto.allowApproverSameUser),
    useExcludeApproverUser: dto.excludeApproverUser && dto.excludeApproverUser.length > 0,
    excludeApproverUser: dto.excludeApproverUser,
  };
}
function toCommonSettingEntity(dto: CommonSettingDto): CommonSettingEntity {
  return {
    ...toSettingEntity(_.omit(dto, ['locale'])),
    locale: dto.locale,
  };
}
function toNotiSettingEntity(dto: NotiSettingDto): NotiSettingEntity {
  const useLongTermPeddingApprovalNotificationDay =
    dto.longTermPeddingApprovalNotificationDay !== -1;

  return {
    ...toSettingEntity(
      _.omit(dto, ['useMail', 'useFpns', 'useWeb', 'longTermPeddingApprovalNotificationDay'])
    ),
    useMail: toBoolean(dto.useMail),
    useFpns: toBoolean(dto.useFpns),
    useWeb: toBoolean(dto.useWeb),
    useLongTermPeddingApprovalNotificationDay,
    longTermPeddingApprovalNotificationDay: useLongTermPeddingApprovalNotificationDay
      ? dto.longTermPeddingApprovalNotificationDay
      : DEFAULT_SETTING_ENTITIES.noti.longTermPeddingApprovalNotificationDay,
  };
}
function toProxySettingEntity(dto: ProxySettingDto): ProxySettingEntity {
  const useLimitDay = dto.limitDay !== -1;
  const useLimitPerson = dto.limitPerson !== -1;

  return {
    ...toSettingEntity(_.omit(dto, ['limitDay', 'limitPerson', 'allowAllDept'])),
    useLimitDay,
    limitDay: useLimitDay ? dto.limitDay : DEFAULT_SETTING_ENTITIES.proxy.limitDay,
    useLimitPerson,
    limitPerson: useLimitPerson ? dto.limitPerson : DEFAULT_SETTING_ENTITIES.proxy.limitPerson,
    allowAllDept: toBoolean(dto.allowAllDept),
  };
}
function toDecryptSettingEntity(dto: DecryptSettingDto): DecryptSettingEntity {
  const useTotalFileSizeMB = dto.totalFileSizeMB !== -1;
  const useMaxTotalFiles = dto.maxTotalFiles !== -1;

  return {
    ...toSettingEntity(
      _.omit(dto, [
        'useSubmission',
        'totalFileSizeMB',
        'storagePeriod',
        'useAllowExtensionYn',
        'allowExtensions',
        'useOrder',
        'maxTotalFiles',
      ])
    ),
    useSubmission: toBoolean(dto.useSubmission),
    useTotalFileSizeMB,
    totalFileSizeMB: useTotalFileSizeMB
      ? dto.totalFileSizeMB
      : DEFAULT_SETTING_ENTITIES.decrypt.totalFileSizeMB,
    storagePeriod: dto.storagePeriod,
    useAllowExtension: toBoolean(dto.useAllowExtensionYn),
    allowExtensions: dto.allowExtensions.map((extension) => extension.replace('.', '')),
    useOrder: toBoolean(dto.useOrder),
    maxTotalFiles: useMaxTotalFiles
      ? dto.maxTotalFiles
      : DEFAULT_SETTING_ENTITIES.decrypt.maxTotalFiles,
  };
}
function toProvisionalSettingEntity(dto: ProvisionalSettingDto): ProvisionalSettingEntity {
  const useTotalFileSizeMB = dto.totalFileSizeMB !== -1;

  return {
    ...toSettingEntity(_.omit(dto, ['totalFileSizeMB', 'storagePeriod', 'useAttachFile'])),
    useTotalFileSizeMB,
    totalFileSizeMB: useTotalFileSizeMB
      ? dto.totalFileSizeMB
      : DEFAULT_SETTING_ENTITIES.provisional.totalFileSizeMB,
    storagePeriod: dto.storagePeriod,
    useAttachFile: toBoolean(dto.useAttachFile),
  };
}
function toScreenSettingEntity(dto: ScreenSettingDto): ScreenSettingEntity {
  const useLimitPeriod = dto.limit !== -1;

  return {
    ...toSettingEntity(_.omit(dto, ['limit', 'unit'])),
    useLimitPeriod: useLimitPeriod,
    limitPeriod: useLimitPeriod ? dto.limit : DEFAULT_SETTING_ENTITIES.screen.limitPeriod,
    unit: dto.unit,
  };
}
function toTakeoutSettingEntity(dto: TakeoutSettingDto): TakeoutSettingEntity {
  const useLimitPeriod = dto.limit !== -1;

  return {
    ...toSettingEntity(_.omit(dto, ['limit', 'unit'])),
    useLimitPeriod: useLimitPeriod,
    limitPeriod: useLimitPeriod ? dto.limit : DEFAULT_SETTING_ENTITIES.takeout.limitPeriod,
    unit: dto.unit,
  };
}
function toPrintPeriodSettingEntity(dto: PrintPeriodSettingDto): PrintPeriodSettingEntity {
  const useLimitPeriod = dto.limit !== -1;

  return {
    ...toSettingEntity(_.omit(dto, ['limit', 'unit'])),
    useLimitPeriod: useLimitPeriod,
    limitPeriod: useLimitPeriod ? dto.limit : DEFAULT_SETTING_ENTITIES.takeout.limitPeriod,
    unit: dto.unit,
  };
}
function toPrintFileSettingEntity(dto: PrintFileSettingDto): PrintFileSettingEntity {
  const useTotalFileSizeMB = dto.totalFileSizeMB !== -1;
  const useMaxTotalFiles = dto.maxTotalFiles !== -1;

  return {
    ...toSettingEntity(
      _.omit(dto, [
        'useSubmission',
        'totalFileSizeMB',
        'storagePeriod',
        'useAllowExtensionYn',
        'allowExtensions',
        'maxTotalFiles',
        'dsdCode',
      ])
    ),
    useSubmission: toBoolean(dto.useSubmission),
    useTotalFileSizeMB,
    totalFileSizeMB: useTotalFileSizeMB
      ? dto.totalFileSizeMB
      : DEFAULT_SETTING_ENTITIES.decrypt.totalFileSizeMB,
    storagePeriod: dto.storagePeriod,
    useAllowExtension: toBoolean(dto.useAllowExtensionYn),
    allowExtensions: dto.allowExtensions.map((extension) => extension.replace('.', '')),
    maxTotalFiles: useMaxTotalFiles
      ? dto.maxTotalFiles
      : DEFAULT_SETTING_ENTITIES.decrypt.maxTotalFiles,
    dsdCode: dto.dsdCode,
  };
}

// 인쇄 실행 시의 유효 기간 설정은 항상 선택되어야 함
function toPrintClientSettingEntity(dto: PrintClientSettingDto): PrintClientSettingEntity {
  return {
    ...toSettingEntity(_.omit(dto, ['limit', 'unit'])),
    useLimitPeriod: true,
    limitPeriod: dto.limit,
    unit: dto.unit,
  };
}

// function toReqDto(
//   name: string,
//   entity:
//     | AdminSettingReqEntity
//     | ApplySettingReqEntity
//     | CommonSettingReqEntity
//     | NotiSettingReqEntity
//     | ProxySettingReqEntity
//     | DecryptSettingReqEntity
//     | ProvisionalSettingReqEntity
//     | ScreenSettingReqEntity
//     | TakeoutSettingReqEntity
// ): SettingReqDto {
//   return { ...entity, name };
// }
function toSettingReqDto(name: string, reqEntity: SettingReqEntity): SettingReqDto {
  return {
    ..._.omit(reqEntity, ['enabled']),
    name,
    useYn: toUseYN(reqEntity.enabled),
  };
}
function toAdminSettingReqDto(reqEntity: AdminSettingReqEntity): AdminSettingReqDto {
  return {
    ...toSettingReqDto('ADMIN', _.omit(reqEntity, ['useUrgency'])),
    useUrgency: toUseYN(reqEntity.useUrgency),
  };
}
function toApplySettingReqDto(reqEntity: ApplySettingReqEntity): ApplySettingReqDto {
  return {
    ...toSettingReqDto(
      'APPLY',
      _.omit(reqEntity, ['allowApproverSameUser', 'useExcludeApproverUser', 'excludeApproverUser'])
    ),
    allowApproverSameUser: toUseYN(reqEntity.allowApproverSameUser),
    excludeApproverUser: reqEntity.useExcludeApproverUser ? reqEntity.excludeApproverUser : [],
  };
}
function toCommonSettingReqDto(reqEntity: CommonSettingReqEntity): CommonSettingReqDto {
  return {
    ...toSettingReqDto('COMMON', _.omit(reqEntity, ['locale'])),
    locale: reqEntity.locale,
  };
}
function toNotiSettingReqDto(reqEntity: NotiSettingReqEntity): NotiSettingReqDto {
  return {
    ...toSettingReqDto(
      'NOTI',
      _.omit(reqEntity, [
        'useMail',
        'useFpns',
        'useWeb',
        'useLongTermPeddingApprovalNotificationDay',
        'longTermPeddingApprovalNotificationDay',
      ])
    ),

    useMail: toUseYN(reqEntity.enabled && reqEntity.useMail),
    useFpns: toUseYN(reqEntity.enabled && reqEntity.useFpns),
    useWeb: toUseYN(reqEntity.enabled && reqEntity.useWeb),
    longTermPeddingApprovalNotificationDay:
      reqEntity.enabled && reqEntity.useLongTermPeddingApprovalNotificationDay
        ? reqEntity.longTermPeddingApprovalNotificationDay
        : -1,
  };
}
function toProxySettingReqDto(reqEntity: ProxySettingReqEntity): ProxySettingReqDto {
  return {
    ...toSettingReqDto(
      'PROXY',
      _.omit(reqEntity, [
        'useLimitDay',
        'limitDay',
        'useLimitPerson',
        'limitPerson',
        'allowAllDept',
      ])
    ),
    limitDay: reqEntity.useLimitDay ? reqEntity.limitDay : -1,
    limitPerson: reqEntity.useLimitPerson ? reqEntity.limitPerson : -1,
    allowAllDept: toUseYN(reqEntity.allowAllDept),
  };
}
function toDecryptSettingReqDto(reqEntity: DecryptSettingReqEntity): DecryptSettingReqDto {
  return {
    ...toSettingReqDto(
      'DECRYPT',
      _.omit(reqEntity, [
        'useSubmission',
        'useTotalFileSizeMB',
        'totalFileSizeMB',
        'storagePeriod',
        'useAllowExtension',
        'allowExtensions',
        'useOrder',
        'maxTotalFiles',
      ])
    ),
    useSubmission: toUseYN(reqEntity.useSubmission),
    totalFileSizeMB: reqEntity.useTotalFileSizeMB ? reqEntity.totalFileSizeMB : -1,
    storagePeriod: reqEntity.storagePeriod,
    useAllowExtensionYn: toUseYN(reqEntity.useAllowExtension),
    allowExtensions: reqEntity.allowExtensions.map((extension) => extension.replace('.', '')),
    useOrder: toUseYN(reqEntity.useOrder),
    maxTotalFiles: reqEntity.maxTotalFiles ? reqEntity.maxTotalFiles : -1,
  };
}
function toProvisionalSettingReqDto(
  reqEntity: ProvisionalSettingReqEntity
): ProvisionalSettingReqDto {
  return {
    ...toSettingReqDto(
      'PROVISIONAL',
      _.omit(reqEntity, ['useTotalFileSizeMB', 'totalFileSizeMB', 'storagePeriod', 'useAttachFile'])
    ),
    totalFileSizeMB: reqEntity.useTotalFileSizeMB ? reqEntity.totalFileSizeMB : -1,
    storagePeriod: reqEntity.storagePeriod,
    useAttachFile: toUseYN(reqEntity.useAttachFile),
  };
}
function toScreenSettingReqDto(reqEntity: ScreenSettingReqEntity): ScreenSettingReqDto {
  return {
    ...toSettingReqDto('SCREEN', _.omit(reqEntity, ['useLimitPeriod', 'limitPeriod', 'unit'])),
    limit: reqEntity.useLimitPeriod ? reqEntity.limitPeriod : -1,
    unit: reqEntity.unit,
  };
}
function toTakeoutSettingReqDto(reqEntity: TakeoutSettingReqEntity): TakeoutSettingReqDto {
  return {
    ...toSettingReqDto('TAKEOUT', _.omit(reqEntity, ['useLimitPeriod', 'limitPeriod', 'unit'])),
    limit: reqEntity.useLimitPeriod ? reqEntity.limitPeriod : -1,
    unit: reqEntity.unit,
  };
}
function toPrintPeriodSettingReqDto(
  reqEntity: PrintPeriodSettingReqEntity
): PrintPeriodSettingReqDto {
  return {
    ...toSettingReqDto('PRINTPOLICY', _.omit(reqEntity, ['useLimitPeriod', 'limitPeriod', 'unit'])),
    limit: reqEntity.useLimitPeriod ? reqEntity.limitPeriod : -1,
    unit: reqEntity.unit,
  };
}
function toPrintFileSettingReqDto(reqEntity: PrintFileSettingReqEntity): PrintFileSettingReqDto {
  return {
    ...toSettingReqDto(
      'PRINTFILE',
      _.omit(reqEntity, [
        'useSubmission',
        'useTotalFileSizeMB',
        'totalFileSizeMB',
        'storagePeriod',
        'useAllowExtension',
        'allowExtensions',
        'maxTotalFiles',
        'dsdCode',
      ])
    ),
    useSubmission: toUseYN(reqEntity.useSubmission),
    totalFileSizeMB: reqEntity.useTotalFileSizeMB ? reqEntity.totalFileSizeMB : -1,
    storagePeriod: reqEntity.storagePeriod,
    useAllowExtensionYn: toUseYN(reqEntity.useAllowExtension),
    allowExtensions: reqEntity.allowExtensions.map((extension) => extension.replace('.', '')),
    maxTotalFiles: reqEntity.maxTotalFiles ? reqEntity.maxTotalFiles : -1,
    dsdCode: reqEntity.dsdCode ? reqEntity.dsdCode : '',
  };
}

function toPrintClientSettingReqDto(
  reqEntity: PrintClientSettingReqEntity
): PrintClientSettingReqDto {
  return {
    ...toSettingReqDto('PRINTCLIENT', _.omit(reqEntity, ['useLimitPeriod', 'limitPeriod', 'unit'])),
    limit: reqEntity.useLimitPeriod ? reqEntity.limitPeriod : -1,
    unit: reqEntity.unit,
  };
}

interface SettingState {
  initialized: boolean;
  entities: SettingEntities;
}

// # initial state
function createDefaultSettingEntities(): SettingEntities {
  return {
    admin: {
      enabled: true,
      useUrgency: true,
      etc: null,
    },
    apply: {
      enabled: true,
      allowApproverSameUser: true,
      useExcludeApproverUser: false,
      excludeApproverUser: [],
      etc: null,
    },
    common: { enabled: true, locale: 'ko', etc: null },
    noti: {
      enabled: false,
      useMail: false,
      useFpns: false,
      useWeb: false,
      useLongTermPeddingApprovalNotificationDay: false,
      longTermPeddingApprovalNotificationDay: 7,
      etc: null,
    },
    proxy: {
      enabled: false,
      useLimitDay: false,
      limitDay: 1,
      useLimitPerson: false,
      limitPerson: 1,
      allowAllDept: false,
      etc: null,
    },
    decrypt: {
      enabled: false,
      useSubmission: false,
      useTotalFileSizeMB: false,
      totalFileSizeMB: 100,
      storagePeriod: 180,
      maxTotalFiles: 100,
      useAllowExtension: false,
      allowExtensions: [],
      useOrder: false,
      etc: null,
    },
    provisional: {
      enabled: false,
      useTotalFileSizeMB: false,
      totalFileSizeMB: 100,
      storagePeriod: 180,
      useAttachFile: false,
      etc: null,
    },
    screen: { enabled: false, useLimitPeriod: false, limitPeriod: 1, unit: 'DAY', etc: null },
    takeout: { enabled: false, useLimitPeriod: false, limitPeriod: 1, unit: 'DAY', etc: null },
    printPeriod: { enabled: false, useLimitPeriod: false, limitPeriod: 1, unit: 'DAY', etc: null },
    printFile: {
      enabled: false,
      useSubmission: false,
      totalFileSizeMB: 100,
      storagePeriod: 180,
      maxTotalFiles: 30,
      useAllowExtension: false,
      useTotalFileSizeMB: false,
      allowExtensions: [],
      etc: null,
      dsdCode: '',
    },
    printClient: { enabled: false, useLimitPeriod: true, limitPeriod: 30, unit: 'DAY', etc: null },
  };
}
const initialState: SettingState = {
  initialized: false,
  entities: createDefaultSettingEntities(),
};

// # thunks
export const thunkLoadSettings = createAsyncThunk('setting/load', async () => {
  const [commonSettingApiDtos, subSettingApiDtos] = await Promise.all([
    settingApi.listCommon(),
    settingApi.listSub(),
  ]);
  return toSettingEntities(commonSettingApiDtos, subSettingApiDtos);
});
export const thunkUpdateAdminSetting = createAsyncThunk(
  'setting/updateAdmin',
  async (reqEntity: AdminSettingReqEntity) => {
    const apiDto = await settingApi.updateCommon(toAdminSettingReqDto(reqEntity));
    return toAdminSettingEntity(apiDto.globalSetting as AdminSettingDto);
  }
);
export const thunkUpdateApplySetting = createAsyncThunk(
  'setting/updateApply',
  async (reqEntity: ApplySettingReqEntity) => {
    const apiDto = await settingApi.updateCommon(toApplySettingReqDto(reqEntity));
    return toApplySettingEntity(apiDto.globalSetting as ApplySettingDto);
  }
);
export const thunkUpdateCommonSetting = createAsyncThunk(
  'setting/updateCommon',
  async (reqEntity: CommonSettingReqEntity) => {
    const apiDto = await settingApi.updateCommon(toCommonSettingReqDto(reqEntity));
    return toCommonSettingEntity(apiDto.globalSetting as CommonSettingDto);
  }
);
export const thunkUpdateNotiSetting = createAsyncThunk(
  'setting/updateNoti',
  async (reqEntity: NotiSettingReqEntity) => {
    const apiDto = await settingApi.updateCommon(toNotiSettingReqDto(reqEntity));
    return toNotiSettingEntity(apiDto.globalSetting as NotiSettingDto);
  }
);
export const thunkUpdateProxySetting = createAsyncThunk(
  'setting/updateProxy',
  async (reqEntity: ProxySettingReqEntity) => {
    const apiDto = await settingApi.updateCommon(toProxySettingReqDto(reqEntity));
    return toProxySettingEntity(apiDto.globalSetting as ProxySettingDto);
  }
);
export const thunkUpdateDecryptSetting = createAsyncThunk(
  'setting/updateDecrypt',
  async (reqEntity: DecryptSettingReqEntity) => {
    const apiDto = await settingApi.updateSub(toDecryptSettingReqDto(reqEntity));
    return toDecryptSettingEntity(apiDto.globalSubSetting as DecryptSettingDto);
  }
);
export const thunkUpdateProvisionalSetting = createAsyncThunk(
  'setting/updateProvisional',
  async (reqEntity: ProvisionalSettingReqEntity) => {
    const apiDto = await settingApi.updateSub(toProvisionalSettingReqDto(reqEntity));
    return toProvisionalSettingEntity(apiDto.globalSubSetting as ProvisionalSettingDto);
  }
);
export const thunkUpdateScreenSetting = createAsyncThunk(
  'setting/updateScreen',
  async (reqEntity: ScreenSettingReqEntity) => {
    const apiDto = await settingApi.updateSub(toScreenSettingReqDto(reqEntity));
    return toScreenSettingEntity(apiDto.globalSubSetting as ScreenSettingDto);
  }
);
export const thunkUpdateTakeoutSetting = createAsyncThunk(
  'setting/updateTakeout',
  async (reqEntity: TakeoutSettingReqEntity) => {
    const apiDto = await settingApi.updateSub(toTakeoutSettingReqDto(reqEntity));
    return toTakeoutSettingEntity(apiDto.globalSubSetting as TakeoutSettingDto);
  }
);
export const thunkUpdatePrintPeriodSetting = createAsyncThunk(
  'setting/updatePrintPolicy',
  async (reqEntity: PrintPeriodSettingReqEntity) => {
    const apiDto = await settingApi.updateSub(toPrintPeriodSettingReqDto(reqEntity));
    return toPrintPeriodSettingEntity(apiDto.globalSubSetting as PrintPeriodSettingDto);
  }
);
export const thunkUpdatePrintFileSetting = createAsyncThunk(
  'setting/updatePrintFile',
  async (reqEntity: PrintFileSettingReqEntity) => {
    const apiDto = await settingApi.updateSub(toPrintFileSettingReqDto(reqEntity));
    return toPrintFileSettingEntity(apiDto.globalSubSetting as PrintFileSettingDto);
  }
);
export const thunkUpdatePrintClientSetting = createAsyncThunk(
  'setting/updatePrintClient',
  async (reqEntity: PrintClientSettingReqEntity) => {
    const apiDto = await settingApi.updateSub(toPrintClientSettingReqDto(reqEntity));
    return toPrintClientSettingEntity(apiDto.globalSubSetting as PrintClientSettingDto);
  }
);

const settingSlice = createSlice({
  name: 'setting',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(thunkLoadSettings.fulfilled, (state, { payload: settingEntities }) => {
      state.entities = settingEntities;
      state.initialized = true;
    });
    builder.addCase(thunkUpdateAdminSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.admin = entity;
    });
    builder.addCase(thunkUpdateApplySetting.fulfilled, (state, { payload: entity }) => {
      state.entities.apply = entity;
    });
    builder.addCase(thunkUpdateCommonSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.common = entity;
    });
    builder.addCase(thunkUpdateNotiSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.noti = entity;
    });
    builder.addCase(thunkUpdateProxySetting.fulfilled, (state, { payload: entity }) => {
      state.entities.proxy = entity;
    });
    builder.addCase(thunkUpdateDecryptSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.decrypt = entity;
    });
    builder.addCase(thunkUpdateProvisionalSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.provisional = entity;
    });
    builder.addCase(thunkUpdateScreenSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.screen = entity;
    });
    builder.addCase(thunkUpdateTakeoutSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.takeout = entity;
    });
    builder.addCase(thunkUpdatePrintPeriodSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.printPeriod = entity;
    });
    builder.addCase(thunkUpdatePrintFileSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.printFile = entity;
    });
    builder.addCase(thunkUpdatePrintClientSetting.fulfilled, (state, { payload: entity }) => {
      state.entities.printClient = entity;
    });
  },
});

// # selectors
export const selectInitialized = (state: RootState): boolean => {
  return state.setting.initialized;
};
export const selectSettingEntities = (state: RootState): SettingEntities => state.setting.entities;

// # etc
export const DEFAULT_SETTING_ENTITIES: DeepReadonly<SettingEntities> =
  createDefaultSettingEntities();

// # default
export default settingSlice.reducer;
